import React, { useEffect, useRef, useState } from 'react';
import { Box, FormLabel, HStack, IconButton, Input, VStack, Button, FormControl, FormErrorMessage } from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

interface Props {
  label: string;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  // SL_004: report whether all rows are complete so the parent can block submit.
  onValidityChange?: (valid: boolean) => void;
}

// SL_004: a row is incomplete when exactly one of key/value is filled.
const rowIncomplete = (r: Row) => {
  const k = r.k.trim();
  const v = r.v.trim();
  return (k !== '' && v === '') || (k === '' && v !== '');
};

interface Row {
  k: string;
  v: string;
}

const rowsFromMap = (map: Record<string, string>): Row[] =>
  Object.entries(map).map(([k, v]) => ({ k, v }));

const mapFromRows = (rows: Row[]): Record<string, string> => {
  const out: Record<string, string> = {};
  rows.forEach((r) => {
    const k = r.k.trim();
    if (k) out[k] = r.v;
  });
  return out;
};

// Edits a string→string map. Rows live in LOCAL state so a freshly-added
// empty-key row survives until the user types into it — the parent's
// Record<string,string> cannot represent an empty key, so deriving rows
// from props made the Add button silently no-op.
export const KeyValueEditor: React.FC<Props> = ({ label, value, onChange, onValidityChange }) => {
  const [rows, setRows] = useState<Row[]>(() => rowsFromMap(value));
  // Last map WE emitted upward — used to distinguish parent-driven resets
  // (modal close/reopen, prefill from sibling fields) from our own updates.
  const lastEmitted = useRef<string>(JSON.stringify(value));

  // Re-sync FROM parent only when parent changes externally.
  useEffect(() => {
    const serialized = JSON.stringify(value);
    if (serialized !== lastEmitted.current) {
      setRows(rowsFromMap(value));
      lastEmitted.current = serialized;
    }
  }, [value]);

  const emit = (next: Row[]) => {
    setRows(next);
    const map = mapFromRows(next);
    lastEmitted.current = JSON.stringify(map);
    onChange(map);
  };

  const update = (idx: number, patch: Partial<Row>) => {
    emit(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // Adding a row is LOCAL only — don't emit until the user types a key.
  // Otherwise the parent state stays the same and the row would vanish.
  const add = () => setRows((cur) => [...cur, { k: '', v: '' }]);

  const remove = (idx: number) => emit(rows.filter((_, i) => i !== idx));

  // SL_004: tell the parent whether every row is complete (both key and value).
  useEffect(() => {
    onValidityChange?.(!rows.some(rowIncomplete));
    // onValidityChange identity is caller's responsibility (useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <Box>
      <FormLabel mb={2}>{label}</FormLabel>
      <VStack spacing={2} align="stretch">
        {rows.map((row, idx) => {
          // SL_004: both halves are required — flag whichever is missing inline.
          const keyMissing = row.v.trim() !== '' && row.k.trim() === '';
          const valueMissing = row.k.trim() !== '' && row.v.trim() === '';
          return (
            <HStack key={idx} spacing={2} align="flex-start">
              <FormControl isInvalid={keyMissing}>
                <Input
                  size="sm"
                  placeholder="key"
                  value={row.k}
                  onChange={(e) => update(idx, { k: e.target.value })}
                />
                <FormErrorMessage fontSize="xs">A key is required for this value.</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={valueMissing}>
                <Input
                  size="sm"
                  placeholder="value"
                  value={row.v}
                  onChange={(e) => update(idx, { v: e.target.value })}
                />
                <FormErrorMessage fontSize="xs">A value is required for this key.</FormErrorMessage>
              </FormControl>
              <IconButton
                aria-label="Remove"
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => remove(idx)}
              />
            </HStack>
          );
        })}
        <Button leftIcon={<FiPlus />} size="xs" variant="outline" alignSelf="flex-start" onClick={add}>
          Add {label.toLowerCase()}
        </Button>
      </VStack>
    </Box>
  );
};
